import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import StatCounter from "@/components/StatCounter";
import Reveal, { staggerParent, fadeUp } from "@/components/Reveal";
import TestimonialCarousel from "@/components/TestimonialCarousel";
import CTABanner from "@/components/CTABanner";
import {
  brand,
  trustHighlights,
  advisoryAreas,
  clarityFramework,
  consultingServices,
  pageMeta,
} from "@/data/mockData";
import { stripHtml, truncate } from "@/lib/format";
import { useCaseStudies } from "@/lib/content";

export default function Home() {
  const { data: caseStudies } = useCaseStudies();
  return (
    <>
      <SEO {...pageMeta.home} />

      {/* ============ HERO ============ */}
      <section
        className="relative bg-white pt-12 md:pt-20 pb-16 md:pb-24 overflow-hidden"
        data-testid="hero-section"
      >
        {/* Subtle decorative shapes */}
        <div className="absolute top-10 right-10 h-72 w-72 rounded-full bg-[#fffbea] blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 h-72 w-72 rounded-full bg-[#f8fafc] blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.05fr,1fr] gap-14 items-center">
            <motion.div initial="hidden" animate="show" variants={staggerParent}>
              <motion.div
                variants={fadeUp}
                className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#0a2540]/70 font-semibold mb-6"
              >
                <span className="h-px w-10 bg-[#ffde59]" />
                Singapore Real Estate Advisory · Since 2012
              </motion.div>
              <motion.h1
                variants={fadeUp}
                className="font-serif text-[#0a2540] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.05]"
              >
                Strategic Property Advisory for{" "}
                <span className="italic text-[#0a2540]/90">Smarter Wealth</span> Decisions.
              </motion.h1>
              <motion.p
                variants={fadeUp}
                className="mt-7 text-base md:text-lg text-slate-600 max-w-2xl leading-relaxed"
              >
                Helping homeowners, HDB upgraders, families, and investors make clearer property decisions through data-driven planning, asset progression, and long-term wealth strategies.
              </motion.p>
              <motion.div variants={fadeUp} className="mt-9 flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  data-testid="hero-schedule-btn"
                  className="inline-flex items-center gap-2 bg-[#ffde59] text-[#023e8a] px-7 py-4 font-semibold text-base rounded-full hover:shadow-xl hover:-translate-y-0.5 transition-all"
                >
                  Schedule an Appointment
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
                <Link
                  to="/about"
                  data-testid="hero-framework-btn"
                  className="inline-flex items-center gap-2 border border-[#0a2540]/20 text-[#0a2540] px-7 py-4 font-medium text-base rounded-full hover:border-[#0a2540] transition-all"
                >
                  Knowing Johnny's Story
                </Link>
              </motion.div>
              <motion.div variants={fadeUp} className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-xs text-slate-500">
                <span className="flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-[#0a2540]" aria-hidden="true" />
                  CEA Registration {brand.cea}
                </span>
              </motion.div>
            </motion.div>

            {/* Johnny portrait */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative max-w-md mx-auto">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#fffbea] to-white rounded-3xl -z-10" />
                <div className="relative animate-floaty">
                  <div className="rounded-3xl overflow-hidden bg-[#fafafa]">
                    <img
                      src="/johnny/formal-1.jpg"
                      alt="Johnny Tan — Singapore property advisor"
                      className="w-full object-cover"
                      loading="eager"
                    />
                  </div>
                  <div className="absolute -bottom-6 -left-6 bg-white border border-gray-100 shadow-xl px-5 py-4 rounded-2xl max-w-[220px]">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
                      Property Advisor
                    </div>
                    <div className="font-serif text-[#0a2540] text-lg leading-tight mt-0.5">
                      Johnny Tan · 陈健盛
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      CEA {brand.cea}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ STATS STRIP ============ */}
      <section className="bg-[#fafafa] py-14 border-y border-gray-100" data-testid="stats-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-10 divide-y md:divide-y-0 md:divide-x divide-gray-200">
          {trustHighlights.map((t, i) => (
            <div key={i} className={`text-center ${i > 0 ? "pt-6 md:pt-0 md:pl-6" : ""}`}>
              <StatCounter value={t.value} suffix={t.suffix} label={t.label} static={t.static} />
            </div>
          ))}
        </div>
      </section>

      {/* ============ INTRO ============ */}
      <section className="bg-white py-20 md:py-28" data-testid="intro-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#0a2540]/60 font-semibold mb-4">
              Meet Your Advisor
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0a2540] leading-[1.1]">
              Property is more than buying and selling.<br />
              <span className="italic">It is about building long-term wealth — done right.</span>
            </h2>
            <p className="mt-8 text-base md:text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto">
              Johnny Tan has guided first-time buyers, HDB upgraders, landlords, and investors through Singapore's property market since 2012 — with a focus on clarity, timing, and exit strategy.
            </p>
            <div className="mt-10">
              <Link
                to="/about"
                data-testid="intro-about-link"
                className="inline-flex items-center gap-2 text-[#0a2540] font-medium link-underline"
              >
                Read Johnny's story
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ ADVISORY AREAS ============ */}
      <section className="bg-[#fafafa] py-20 md:py-28" data-testid="advisory-areas">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-3xl mx-auto mb-14">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#0a2540]/60 font-semibold mb-4">
              Areas of Advisory
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0a2540] leading-[1.1]">
              Specialised guidance for every stage.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {advisoryAreas.map((a, i) => (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="advisory-card group relative bg-white border border-gray-100 rounded-2xl p-5 flex items-center gap-5"
                data-testid={`advisory-${a.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {/* Circular icon — gold ring already baked into the PNG */}
                <div className="shrink-0 h-20 w-20 rounded-full overflow-hidden flex items-center justify-center bg-transparent">
                  <img
                    src={a.iconPng}
                    alt={a.alt}
                    loading="lazy"
                    decoding="async"
                    draggable="false"
                    className="advisory-icon h-full w-full object-contain select-none"
                    data-testid={`advisory-icon-${i}`}
                  />
                </div>
                {/* Text */}
                <div className="min-w-0">
                  <h3 className="font-serif text-lg text-[#0a2540] mb-1 leading-tight">{a.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{a.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FRAMEWORK PREVIEW ============ */}
      <section className="bg-white py-20 md:py-28 relative" data-testid="framework-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1fr,1.4fr] gap-14 items-start">
            <Reveal>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#0a2540]/60 font-semibold mb-4">
                The Framework
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0a2540] leading-[1.1] mb-6">
                The C.L.A.R.I.T.Y Property System.
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-8">
                A seven-step advisory process Johnny developed over a decade of transactions — designed to bring clarity, structure, and confidence to every property decision.
              </p>
              <Link
                to="/framework"
                data-testid="framework-preview-link"
                className="inline-flex items-center gap-2 bg-[#ffde59] text-[#023e8a] px-6 py-3 font-semibold text-sm rounded-full hover:shadow-lg transition-all"
              >
                Explore the Framework
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
            </Reveal>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" role="list" aria-label="C.L.A.R.I.T.Y framework steps">
              {clarityFramework.map((f, i) => (
                <motion.article
                  key={f.letter}
                  role="listitem"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="clarity-card group bg-[#fafafa] border border-gray-100 rounded-2xl p-4 pt-6 flex flex-col items-center text-center overflow-visible"
                  data-testid={`clarity-${f.letter.toLowerCase()}`}
                >
                  <div className="font-serif text-4xl text-[#0a2540] leading-none">{f.letter}</div>
                  <span className="block mx-auto mt-2 h-px w-6 bg-[#ffde59] clarity-rule" aria-hidden="true" />
                  <picture>
                    <source srcSet={f.iconWebp} type="image/webp" />
                    <img
                      src={f.iconPng}
                      alt={f.alt}
                      width="64"
                      height="64"
                      loading="lazy"
                      decoding="async"
                      draggable="false"
                      className="clarity-icon w-full max-w-[90px] h-auto mt-3 mb-2 select-none will-change-transform transition-transform duration-150 ease-out"
                    />
                  </picture>
                  <h3 className="font-sans text-[11px] sm:text-xs text-slate-700 leading-tight font-semibold">
                    {f.word}
                  </h3>
                </motion.article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ SERVICES PREVIEW ============ */}
      <section className="bg-[#fafafa] py-20 md:py-28" data-testid="services-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#0a2540]/60 font-semibold mb-4">
                Services
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0a2540] leading-[1.1]">
                Consulting that compounds.
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="services-preview-link"
              className="inline-flex items-center gap-2 text-[#0a2540] font-medium link-underline self-start"
            >
              View all services
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {consultingServices.slice(0, 6).map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="service-card group bg-white border border-gray-100 rounded-2xl p-7"
                data-testid={`consulting-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {s.iconPng ? (
                  <picture>
                    <img
                      src={s.iconPng}
                      alt={s.alt || s.title}
                      loading="lazy"
                      decoding="async"
                      draggable="false"
                      className="service-icon h-20 max-w-full object-contain object-left mb-4 select-none will-change-transform transition-transform duration-150 ease-out"
                    />
                  </picture>
                ) : (
                  <div className="h-11 w-11 rounded-full bg-[#fffbea] inline-flex items-center justify-center mb-4">
                    <i className={`${s.icon} text-[#023e8a]`} aria-hidden="true" />
                  </div>
                )}
                <h3 className="font-serif text-lg text-[#0a2540] mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SUCCESS STORIES PREVIEW ============ */}
      <section className="bg-white py-20 md:py-28" data-testid="success-stories-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#0a2540]/60 font-semibold mb-4">
                Success Stories
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0a2540] leading-[1.1]">
                Real outcomes. Real progression.
              </h2>
            </div>
            <Link
              to="/results"
              data-testid="home-results-link"
              className="inline-flex items-center gap-2 text-[#0a2540] font-medium link-underline self-start"
            >
              See all results
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {caseStudies.slice(0, 2).map((c, i) => (
              <motion.article
                key={c.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                className="bg-white border border-gray-100 rounded-2xl overflow-hidden group hover:shadow-xl transition-all"
                data-testid={`home-case-${c.id}`}
              >
                <div className="overflow-hidden bg-gray-100">
                  <img src={c.image} alt={c.headline} loading="lazy" className="w-full h-auto block group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-7">
                  <div className="inline-block bg-[#fffbea] text-[#0a2540] px-3 py-1 text-[10px] font-semibold uppercase tracking-widest rounded-full mb-3">
                    {c.metric}
                  </div>
                  <h3 className="font-serif text-xl text-[#0a2540] mb-3 leading-snug">
                    {c.headline}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed line-clamp-3">{truncate(stripHtml(c.story), 160)}</p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <TestimonialCarousel />

      {/* ============ CTA ============ */}
      <CTABanner />
    </>
  );
}
